import { create } from 'zustand';
import { prepareLineItemProperties, submitOrderWithFiles, validateFile } from '../lib/shopifyApi';
import { 
  ProductType, 
  BeanType, 
  PackageType, 
  BeanVariety, 
  SugarConcentration, 
  LotSize,
  ProductFlowMapping,
  CartItemData,
  OrderFlowState as OrderFlowStateType
} from '../types/orderFlow';
import { useTranslations } from '../hooks/useTranslations';

// ストア用の状態定義
interface OrderFlowState extends OrderFlowStateType {
  // アクション
  setProductType: (type: ProductType | null) => void;
  setBeanType: (type: BeanType | null) => void;
  setPackageType: (type: PackageType | null) => void;
  setSelectedBean: (bean: BeanVariety | null) => void;
  setLotSize: (size: LotSize | null) => void;
  setSugarConcentration: (concentration: SugarConcentration | null) => void;
  
  // デザインオプション設定
  setNeedsInnerDesign: (needs: boolean) => void;
  setNeedsFrontLabel: (needs: boolean) => void;
  setNeedsOuterBox: (needs: boolean) => void;
  setNeedsOuterBoxDesign: (needs: boolean) => void;
  setNeedsBackLabel: (needs: boolean) => void;
  
  // 選択状態フラグ操作
  setHasSelectedInnerDesign: (selected: boolean) => void;
  setHasSelectedFrontLabel: (selected: boolean) => void;
  setHasSelectedOuterBox: (selected: boolean) => void;
  setHasSelectedOuterBoxDesign: (selected: boolean) => void;
  setHasSelectedBackLabel: (selected: boolean) => void;
  
  // ファイルアップロード
  setInnerDesignFile: (file: File | null) => void;
  setFrontLabelFile: (file: File | null) => void;
  setOuterBoxDesignFile: (file: File | null) => void;
  setBackLabelFile: (file: File | null) => void;
  
  // フロー管理
  setCurrentStep: (step: string) => void;
  setAvailableProducts: (products: ProductFlowMapping[]) => void;
  updateCurrentProduct: () => void;
  
  // 価格計算
  calculateTotalPrice: () => void;
  
  // カート管理
  addToCart: (item: CartItemData) => void;
  clearCart: () => void;
  
  // Liquidフォーム連携
  submitOrderWithFiles: () => void;
  validateUploadedFiles: () => { isValid: boolean; errors: string[] };
  
  // リセット
  reset: () => void;
  
  // ユーザー選択状態のリセット
  resetUserSelections: (keys?: (keyof typeof initialState.userSelections)[]) => void;
}

const initialState: OrderFlowStateType = {
  // 基本選択
  productType: null,
  beanType: null,
  packageType: null,
  selectedBean: null,
  lotSize: null,
  
  // 濃縮液・ストレート用オプション
  sugarConcentration: null,
  
  // デザインオプション - 全て未選択状態に統一
  needsInnerDesign: false,
  needsFrontLabel: false,
  needsOuterBox: false,
  needsOuterBoxDesign: false,
  needsBackLabel: false,
  
  // 選択状態追跡フラグ（ユーザーが実際に選択を行ったかどうか）
  userSelections: {
    hasSelectedInnerDesign: false,
    hasSelectedFrontLabel: false,
    hasSelectedOuterBox: false,
    hasSelectedOuterBoxDesign: false,
    hasSelectedBackLabel: false,
  },
  
  // アップロードファイル
  innerDesignFile: null,
  frontLabelFile: null,
  outerBoxDesignFile: null,
  backLabelFile: null,
  
  // 価格情報
  basePrice: 0,
  optionPrices: {},
  totalPrice: 0,
  
  // 現在のステップ
  currentStep: 'select-product-type',
  
  // 利用可能な商品データ
  availableProducts: [],
  currentProduct: null,
  
  // カート用データ
  cartItems: [],
};

export const useOrderFlowStore = create<OrderFlowState>((set, get) => ({
  ...initialState,
  
  // 基本選択アクション
  setProductType: (type) => {
    set({ productType: type });
    if (type) {
      get().updateCurrentProduct();
      get().calculateTotalPrice();
    }
  },
  
  setBeanType: (type) => {
    set({ beanType: type, selectedBean: null }); // 豆タイプが変わったら選択をリセット
    if (type) {
      get().updateCurrentProduct();
      get().calculateTotalPrice();
    }
  },
  
  setPackageType: (type) => {
    set({ packageType: type });
    if (type) {
      get().updateCurrentProduct();
      get().calculateTotalPrice();
    }
  },
  
  setSelectedBean: (bean) => {
    set({ selectedBean: bean });
    get().updateCurrentProduct();
    get().calculateTotalPrice();
  },
  
  setLotSize: (size) => {
    set({ lotSize: size });
    if (size) {
      get().calculateTotalPrice();
    }
  },
  
  setSugarConcentration: (concentration) => {
    set({ sugarConcentration: concentration });
    if (concentration !== null) {
      get().calculateTotalPrice();
    }
  },
  
  // デザインオプション設定
  setNeedsInnerDesign: (needs) => {
    set({ 
      needsInnerDesign: needs,
      userSelections: { 
        ...get().userSelections, 
        hasSelectedInnerDesign: true 
      }
    });
    if (!needs) set({ innerDesignFile: null });
    get().calculateTotalPrice();
  },
  
  setNeedsFrontLabel: (needs) => {
    set({ 
      needsFrontLabel: needs,
      userSelections: { 
        ...get().userSelections, 
        hasSelectedFrontLabel: true 
      }
    });
    if (!needs) set({ frontLabelFile: null });
    get().calculateTotalPrice();
  },
  
  setNeedsOuterBox: (needs) => {
    set({ 
      needsOuterBox: needs,
      userSelections: { 
        ...get().userSelections, 
        hasSelectedOuterBox: true 
      }
    });
    if (!needs) {
      set({ 
        outerBoxDesignFile: null,
        needsOuterBoxDesign: false 
      });
    }
    get().calculateTotalPrice();
  },
  
  setNeedsOuterBoxDesign: (needs) => {
    set({ 
      needsOuterBoxDesign: needs,
      userSelections: { 
        ...get().userSelections, 
        hasSelectedOuterBoxDesign: true 
      }
    });
    if (!needs) set({ outerBoxDesignFile: null });
    get().calculateTotalPrice();
  },
  
  setNeedsBackLabel: (needs) => {
    set({ 
      needsBackLabel: needs,
      userSelections: { 
        ...get().userSelections, 
        hasSelectedBackLabel: true 
      }
    });
    if (!needs) set({ backLabelFile: null });
    get().calculateTotalPrice();
  },
  
  // 選択状態フラグ操作
  setHasSelectedInnerDesign: (selected) => {
    set({ 
      userSelections: { 
        ...get().userSelections, 
        hasSelectedInnerDesign: selected 
      }
    });
  },
  
  setHasSelectedFrontLabel: (selected) => {
    set({ 
      userSelections: { 
        ...get().userSelections, 
        hasSelectedFrontLabel: selected 
      }
    });
  },
  
  setHasSelectedOuterBox: (selected) => {
    set({ 
      userSelections: { 
        ...get().userSelections, 
        hasSelectedOuterBox: selected 
      }
    });
  },
  
  setHasSelectedOuterBoxDesign: (selected) => {
    set({ 
      userSelections: { 
        ...get().userSelections, 
        hasSelectedOuterBoxDesign: selected 
      }
    });
  },
  
  setHasSelectedBackLabel: (selected) => {
    set({ 
      userSelections: { 
        ...get().userSelections, 
        hasSelectedBackLabel: selected 
      }
    });
  },
  
  // ファイルアップロード
  setInnerDesignFile: (file) => set({ innerDesignFile: file }),
  setFrontLabelFile: (file) => set({ frontLabelFile: file }),
  setOuterBoxDesignFile: (file) => set({ outerBoxDesignFile: file }),
  setBackLabelFile: (file) => set({ backLabelFile: file }),
  
  // フロー管理
  setCurrentStep: (step) => set({ currentStep: step }),
  
  setAvailableProducts: (products) => {
    set({ availableProducts: products });
    get().updateCurrentProduct();
  },
  
  updateCurrentProduct: () => {
    const state = get();
    const { productType, beanType, packageType, selectedBean, availableProducts } = state;
    

    
    if (!productType || !beanType || !packageType) {
      set({ currentProduct: null });
      return;
    }
    
    // WAKOの豆を選択した場合は、具体的な銘柄が選択されている必要がある
    if (beanType === 'wako' && !selectedBean) {
      set({ currentProduct: null });
      return;
    }
    
    // 実際の検索条件を決定
    const searchBeanType = beanType === 'wako' ? selectedBean : beanType;
    

    
    const matchingProduct = availableProducts.find(product => 
      product.productType === productType &&
      product.beanType === searchBeanType &&
      product.packageType === packageType
    );
    

    
    set({ currentProduct: matchingProduct || null });
  },
  
  // 価格計算
  calculateTotalPrice: () => {
    const state = get();
    const { currentProduct, lotSize, productType, sugarConcentration } = state;
    

    
    let basePrice = 0;
    let detailedOptionPrices: Record<string, number> = {};
    
    // ベース商品の価格を取得
    if (currentProduct && lotSize) {
      const variant = currentProduct.productInfo.variants.find(v => v.lotSize === lotSize);
      
      if (variant) {
        basePrice = parseFloat(variant.price);
      }
    }
    
    // 砂糖濃度の価格を計算（濃縮液・ストレートの場合）
    let sugarPrice = 0;
    if ((productType === 'liquid-concentrate' || productType === 'straight-coffee') && 
        sugarConcentration && sugarConcentration !== 'none' && 
        currentProduct?.optionSetReference && lotSize) {
      
      const sugarOptions = productType === 'liquid-concentrate' 
        ? currentProduct.optionSetReference.nk_sugar 
        : currentProduct.optionSetReference.st_sugar;
      
      if (sugarOptions) {
        const sugarOption = sugarOptions.find((option: any) => 
          option.title && option.title.includes(sugarConcentration) && option.title.includes(lotSize)
        );
        
        if (sugarOption) {
          sugarPrice = Number(sugarOption.price);
          detailedOptionPrices[`sugar_${sugarConcentration}`] = sugarPrice;
        }
      }
    }
    
    // インナーデザイン価格を計算
    if (state.needsInnerDesign && currentProduct?.optionSetReference && lotSize) {
      const innerOptions = productType === 'freeze-dried' && state.packageType === 'stick'
        ? currentProduct.optionSetReference.fd_inner_design_stick
        : productType === 'freeze-dried' && state.packageType === 'cube'
        ? currentProduct.optionSetReference.fd_front_label_cube
        : null;
      
      if (innerOptions) {
        const innerOption = innerOptions.find((option: any) => 
          option.title && option.title.includes(lotSize)
        );
        if (innerOption) {
          const innerPrice = Number(innerOption.price);
          detailedOptionPrices['inner_design'] = innerPrice;
        }
      }
    }
    
    // 表ラベル価格を計算
    if (state.needsFrontLabel && currentProduct?.optionSetReference && lotSize) {
      const frontOptions = productType === 'liquid-concentrate'
        ? currentProduct.optionSetReference.nk_front_label
        : productType === 'straight-coffee'
        ? currentProduct.optionSetReference.st_front_label
        : null;
      
      if (frontOptions) {
        const frontOption = frontOptions.find((option: any) => 
          option.title && option.title.includes(lotSize)
        );
        if (frontOption) {
          const frontPrice = Number(frontOption.price);
          detailedOptionPrices['front_label'] = frontPrice;
        }
      }
    }
    
    // 外箱価格を計算（プレーン外箱 + デザイン料金の合計）
    if (state.needsOuterBox && currentProduct?.optionSetReference && lotSize) {
      let outerBoxPrice = 0;
      
      // フリーズドライ用の処理
      if (productType === 'freeze-dried') {
        // プレーン外箱の価格を取得
        const plainBoxOptions = currentProduct.optionSetReference.fd_outer_box;
        if (plainBoxOptions) {
          const plainBoxOption = plainBoxOptions.find((option: any) => 
            option.title && option.title.includes(lotSize)
          );
          if (plainBoxOption) {
            outerBoxPrice += Number(plainBoxOption.price) || 0;
          }
        }
        
        // デザイン料金を追加
        if (state.needsOuterBoxDesign) {
          const designOptions = currentProduct.optionSetReference.fd_outer_box_design;
          if (designOptions) {
            const designOption = designOptions.find((option: any) => 
              option.title && option.title.includes(lotSize)
            );
            if (designOption) {
              outerBoxPrice += Number(designOption.price) || 0;
            }
          }
        }
      }
      // 濃縮液・ストレート用の処理
      else if (productType === 'liquid-concentrate' || productType === 'straight-coffee') {
        const outerBoxOptionKey = productType === 'liquid-concentrate' ? 'nk_outer_box' : 'st_outer_box';
        
        // プレーン外箱の価格を取得
        const plainBoxOptions = currentProduct.optionSetReference[outerBoxOptionKey];
        if (plainBoxOptions) {
          const plainBoxOption = plainBoxOptions.find((option: any) => 
            option.title && option.title.includes(lotSize)
          );
          if (plainBoxOption) {
            outerBoxPrice += Number(plainBoxOption.price) || 0;
          }
        }
        
        // デザイン料金を追加
        if (state.needsOuterBoxDesign) {
          const outerBoxDesignOptionKey = productType === 'liquid-concentrate' ? 'nk_outer_box_design' : 'st_outer_box_design';
          const designOptions = currentProduct.optionSetReference[outerBoxDesignOptionKey];
          if (designOptions) {
            const designOption = designOptions.find((option: any) => 
              option.title && option.title.includes(lotSize)
            );
            if (designOption) {
              outerBoxPrice += Number(designOption.price) || 0;
            }
          }
        }
      }
      
      detailedOptionPrices['outer_box'] = outerBoxPrice;
    }
    
    // 裏ラベル価格を計算
    if (state.needsBackLabel && currentProduct?.optionSetReference && lotSize) {
      const backOptions = currentProduct.optionSetReference.fd_back_label_design;
      
      if (backOptions) {
        const backOption = backOptions.find((option: any) => 
          option.title && option.title.includes(lotSize)
        );
        if (backOption) {
          const backPrice = Number(backOption.price);
          detailedOptionPrices['back_label'] = backPrice;
        }
      }
    }
    
    // オプション価格を合計
    const totalOptionPrice = Object.values(detailedOptionPrices).reduce((sum, price) => sum + price, 0);
    const totalPrice = basePrice + totalOptionPrice;
    
    set({ 
      basePrice,
      optionPrices: detailedOptionPrices,
      totalPrice 
    });
  },
  
  // カート管理
  addToCart: (item) => {
    const currentItems = get().cartItems;
    set({ cartItems: [...currentItems, item] });
  },
  
  clearCart: () => set({ cartItems: [] }),
  
  // Liquidフォーム連携
  validateUploadedFiles: () => {
    const state = get();
    const t = useTranslations();
    const errors: string[] = [];
    
    // 必要なファイルがアップロードされているかチェック
    if (state.needsInnerDesign && !state.innerDesignFile) {
      errors.push(t.error_uploadInnerDesign);
    }
    if (state.needsFrontLabel && !state.frontLabelFile) {
      errors.push(t.error_uploadFrontLabel);
    }
    if (state.needsOuterBoxDesign && !state.outerBoxDesignFile) {
      errors.push(t.error_uploadOuterBoxDesign);
    }
    if (state.needsBackLabel && !state.backLabelFile) {
      errors.push(t.error_uploadBackLabel);
    }
    
    // ファイルサイズ・形式の検証
    const filesToValidate = [
      { file: state.innerDesignFile, name: t.file_innerDesign },
      { file: state.frontLabelFile, name: t.file_frontLabel },
      { file: state.outerBoxDesignFile, name: t.file_outerBoxDesign },
      { file: state.backLabelFile, name: t.file_backLabel }
    ].filter(item => item.file);
    
    filesToValidate.forEach(({ file, name }) => {
      if (file) {
        const validation = validateFile(file);
        if (!validation.isValid && validation.error) {
          errors.push(`${name}: ${validation.error}`);
        }
      }
    });
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },
  
  submitOrderWithFiles: () => {
    const state = get();
    const t = useTranslations();
    
    // バリデーション
    const validation = get().validateUploadedFiles();
    if (!validation.isValid) {
      alert(`${t.error_generalError}:\n` + validation.errors.join('\n'));
      return;
    }
    
    // 必要な情報が揃っているかチェック
    if (!state.currentProduct || !state.lotSize) {
      alert(t.error_noProductOrLot);
      return;
    }
    
    // バリアントIDを取得
    const variant = state.currentProduct.productInfo.variants.find(v => v.lotSize === state.lotSize);
    if (!variant) {
      alert(t.error_noVariantFound);
      return;
    }
    
    // LINE ITEM PROPERTYを準備
    const uploadedFiles = {
      innerDesignFile: state.innerDesignFile,
      frontLabelFile: state.frontLabelFile,
      outerBoxDesignFile: state.outerBoxDesignFile,
      backLabelFile: state.backLabelFile
    };
    
    const properties = prepareLineItemProperties(state, uploadedFiles);
    
    // Liquidフォーム経由でカートに追加
    submitOrderWithFiles(
      variant.id,
      properties,
      uploadedFiles
    );
  },
  
  // リセット
  reset: () => {
    const currentProducts = get().availableProducts; // 現在の商品データを保持
    set({
      ...initialState,
      availableProducts: currentProducts // 商品データは保持
    });
    // 商品データが保持されているので、currentProductを再計算
    get().updateCurrentProduct();
  },
  
  // ユーザー選択状態のリセット
  resetUserSelections: (keys) => {
    const currentUserSelections = get().userSelections;
    if (!keys) {
      // 全てのuserSelectionsをリセット
      set({
        userSelections: {
          hasSelectedInnerDesign: false,
          hasSelectedFrontLabel: false,
          hasSelectedOuterBox: false,
          hasSelectedOuterBoxDesign: false,
          hasSelectedBackLabel: false,
        }
      });
    } else {
      // 指定されたキーのみリセット
      const updatedSelections = { ...currentUserSelections };
      keys.forEach(key => {
        updatedSelections[key] = false;
      });
      set({ userSelections: updatedSelections });
    }
  },
}));

// エクスポート（互換性のため）
export type { ProductType, BeanType, PackageType, BeanVariety }; 